/**
 * Seasonal events (the third button of the Stickers section). Shows the currently
 * -claimable {@link EventDoc} — a one-off limited prize — and lets a named user
 * CLAIM it once (the server applies the grant and deduplicates). When there's no
 * live event the tab is "inactive": an honest empty state that says it lights up
 * when an event drops, exactly as the owner specced.
 *
 * Thin orchestration: reads `services.getActiveEvent()` on mount and calls
 * `services.claimEvent(id)` on claim; the SERVER owns the grant + dedup. Anonymous
 * users see the "log in to collect" tease via {@link RequiresAccountError}.
 */
import { useCallback, useEffect, useState } from 'react';
import type { GameServices } from '../services/types';
import { RequiresAccountError } from '../services/types';
import type { EventClaimResult, EventDoc } from '../services/dataModel';
import { useT } from './i18n';

interface SeasonalViewProps {
  services: GameServices;
}

/** Days/hours remaining until the event closes. */
function timeLeft(endsAt: number): string {
  const ms = endsAt - Date.now();
  if (ms <= 0) return 'ending soon';
  const h = Math.floor(ms / 3_600_000);
  if (h >= 48) return `${Math.floor(h / 24)} days left`;
  if (h >= 1) return `${h}h left`;
  return `${Math.max(1, Math.floor(ms / 60_000))}m left`;
}

export function SeasonalView({ services }: SeasonalViewProps) {
  const t = useT();
  /** Human one-liner for an event's prize. */
  const describePrize = (e: EventDoc): string => {
    switch (e.prizeType) {
      case 'tokens':
        return `${e.qty.toLocaleString()} ${t('unit.tokens')}`;
      case 'xp':
        return `${e.qty.toLocaleString()} ${t('unit.xp')}`;
      case 'sticker':
        return t('seasonal.aSticker');
      default:
        return 'a prize';
    }
  };
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState<EventDoc | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [result, setResult] = useState<EventClaimResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [needsAccount, setNeedsAccount] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const e = await services.getActiveEvent();
        if (!cancelled) setEvent(e);
      } catch {
        // Public read; on failure just show the empty state.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [services]);

  const onClaim = useCallback(async () => {
    if (!event) return;
    setClaiming(true);
    setError(null);
    try {
      const res = await services.claimEvent(event.id);
      setResult(res);
    } catch (err) {
      if (err instanceof RequiresAccountError) setNeedsAccount(true);
      else setError(err instanceof Error ? err.message : 'Could not claim the prize.');
    } finally {
      setClaiming(false);
    }
  }, [event, services]);

  return (
    <section className="panel seasonal">
      <style>{SEASONAL_CSS}</style>
      <div className="seasonal-head">
        <span className="seasonal-glyph" aria-hidden>
          🎉
        </span>
        <h2>{t('seasonal.title')}</h2>
      </div>

      {loading ? (
        <p className="seasonal-status">{t('seasonal.checking')}</p>
      ) : needsAccount ? (
        <p className="seasonal-status">{t('seasonal.loginToClaim')}</p>
      ) : !event ? (
        <div className="seasonal-empty">
          <p className="seasonal-empty-title">{t('seasonal.noEventTitle')}</p>
          <p className="seasonal-empty-body">
            {t('seasonal.noEventBody')}
          </p>
        </div>
      ) : (
        <div className="seasonal-event">
          <div className="seasonal-prize">
            <span className="seasonal-prize-label">{t('seasonal.claimLabel')}</span>
            <span className="seasonal-prize-value">{describePrize(event)}</span>
            <span className="seasonal-window">{timeLeft(event.endsAt)}</span>
          </div>

          {result ? (
            <p className="seasonal-result" role="status">
              {result.alreadyClaimed ? t('seasonal.alreadyClaimed') : t('seasonal.claimed')}
            </p>
          ) : (
            <button
              className="btn btn-primary seasonal-claim"
              type="button"
              disabled={claiming}
              onClick={() => void onClaim()}
            >
              {claiming ? t('seasonal.claiming') : t('seasonal.claimPrize')}
            </button>
          )}

          {error && <p className="seasonal-error">{error}</p>}
        </div>
      )}
    </section>
  );
}

const SEASONAL_CSS = `
.seasonal { width: min(440px, 92vw); }
.seasonal .seasonal-head { display: flex; align-items: center; gap: 10px; }
.seasonal .seasonal-glyph { font-size: 22px; }
.seasonal .seasonal-status { color: var(--muted); margin: 12px 0 0; }
.seasonal .seasonal-empty {
  margin-top: 12px;
  padding: 20px;
  text-align: center;
  background: var(--panel-2);
  border: 1px dashed var(--glass-border-strong);
  border-radius: var(--radius);
}
.seasonal .seasonal-empty-title { margin: 0 0 6px; font-weight: 800; color: var(--text); }
.seasonal .seasonal-empty-body { margin: 0; color: var(--muted); font-size: 13px; line-height: 1.5; }
.seasonal .seasonal-event {
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  align-items: center;
}
.seasonal .seasonal-prize {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 22px;
  background: linear-gradient(135deg, var(--accent-tint), var(--panel-2));
  border: 1px solid var(--glass-border-strong);
  border-radius: var(--radius);
}
.seasonal .seasonal-prize-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--muted);
}
.seasonal .seasonal-prize-value {
  font-size: 26px;
  font-weight: 900;
  background: linear-gradient(96deg, var(--accent), var(--gold));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  text-align: center;
}
.seasonal .seasonal-window {
  font-size: 12px;
  color: var(--gold);
  font-weight: 700;
  letter-spacing: 0.04em;
}
.seasonal .seasonal-claim { min-width: 200px; }
.seasonal .seasonal-result { margin: 0; color: var(--success); font-weight: 700; }
.seasonal .seasonal-error { margin: 0; color: var(--danger); font-size: 13px; }
`;
